import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function CustomerContractsTab({ customerId, customerName }) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customerId) {
      fetchContracts();
    }
  }, [customerId]);

  const fetchContracts = async () => {
    try {
      const response = await apiFetch('/api/ppm/contracts');
      const allContracts = await response.json();
      
      // Filter contracts for this customer
      const customerContracts = allContracts.filter(
        contract => contract.customer_id === parseInt(customerId)
      );
      
      setContracts(customerContracts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (value) => {
    if (!value) return '£0.00';
    return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="text-sm">Loading contracts...</div>
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-gray-500 mb-4">
          <div className="text-4xl mb-2">📋</div>
          <p className="text-sm">No PPM contracts for {customerName} yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Create a preventive maintenance contract to manage scheduled servicing
          </p>
        </div>
        <button
          onClick={() => navigate('/ppm-contracts/new')}
          className="mt-4 px-4 py-2 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
        >
          Create PPM Contract
        </button>
      </div>
    );
  }

  const totalAnnualValue = contracts.reduce((sum, c) => sum + parseFloat(c.total_annual_value || 0), 0);
  const activeContracts = contracts.filter(c => c.status === 'active').length;

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">PPM Contracts</h3>
          <p className="text-sm text-gray-600">
            Preventive maintenance contracts for {customerName}
          </p>
        </div>
        <button
          onClick={() => navigate('/ppm-contracts/new')}
          className="px-4 py-2 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
        >
          ➕ New Contract
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-xs font-semibold text-purple-600 uppercase mb-1">Total Contracts</div>
          <div className="text-2xl font-bold text-purple-900">{contracts.length}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-xs font-semibold text-green-600 uppercase mb-1">Active</div>
          <div className="text-2xl font-bold text-green-900">{activeContracts}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-xs font-semibold text-blue-600 uppercase mb-1">Annual Value</div>
          <div className="text-2xl font-bold text-blue-900">{formatCurrency(totalAnnualValue)}</div>
        </div>
      </div>

      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Contract Ref</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Contract Name</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600">Equipment</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Start Date</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">End Date</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Payment Terms</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600">Annual Value</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Status</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {contracts.map((contract, index) => (
              <tr
                key={contract.id}
                className={`hover:bg-blue-50 cursor-pointer transition ${
                  index % 2 === 1 ? 'bg-gray-50' : 'bg-white'
                }`}
                onClick={() => navigate(`/ppm-contracts/${contract.id}`)}
              >
                <td className="px-3 py-2 font-medium text-gray-900">
                  {contract.contract_ref}
                  {contract.renewal_status === 'renewal_due' && (
                    <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                      Renewal Due
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-700">{contract.contract_name}</td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-semibold">
                    {contract.equipment_count || 0}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-600">{formatDate(contract.start_date)}</td>
                <td className="px-3 py-2 text-gray-600">{formatDate(contract.end_date)}</td>
                <td className="px-3 py-2 text-gray-600 text-xs">
                  {contract.payment_terms}
                </td>
                <td className="px-3 py-2 text-right font-medium text-gray-900">
                  {formatCurrency(contract.total_annual_value)}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    contract.status === 'active' ? 'bg-green-100 text-green-800' :
                    contract.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    contract.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                    contract.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {contract.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/ppm-contracts/${contract.id}`);
                    }}
                    className="text-purple-600 hover:text-purple-800 font-medium"
                  >
                    View →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-xs text-gray-600">
        Showing {contracts.length} {contracts.length === 1 ? 'contract' : 'contracts'}
      </div>
    </div>
  );
}

export default CustomerContractsTab;
